import { useEffect, useState } from 'react';
import { getAbTestLifecycleState } from '@bridebook/toolbox/src/ab-testing/lib/selectors';
import { useABTestV2Integration } from '@bridebook/web/lib/ab-tests/hooks/use-ab-test-v2-integration';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { env } from '@bridebook/web/lib/env';
import { IApplicationState } from '@bridebook/web/lib/types';
import { useSelector } from '@bridebook/web/lib/utils';

const testId = 'LIVE-27666_EnquiryFormImprovements';

/**
 * Dev-only localStorage key (toggled from Settings → Dev environment) that lifts the desktop/isMobile
 * gate for the improved enquiry form. Only affects users already in variant `1` — control stays
 * control on every viewport.
 */
export const LIVE_27666_DESKTOP_OVERRIDE_KEY = 'LIVE_27666_EnquiryFormImprovements_desktop';

/**
 * Initializes LIVE-27666 (enquiry form improvements) so lifecycle state and variant are available before gates render.
 *
 * Gate only non-widget enquiry surfaces (e.g. modal). Do not use the variant for embedded profile
 * `isEnquiryFormWidget` or the standalone `enquiry-widget` package.
 */
export const useABTest_LIVE_27666_EnquiryFormImprovements = () => {
  useABTestV2Integration({
    testId,
    triggerCondition: true,
  });
};

export const getVariant_LIVE_27666_EnquiryFormImprovements = (state: IApplicationState) =>
  getAbTestLifecycleState(state, testId)?.variant;

/**
 * Dev override (non-production only): when set in localStorage it lifts the mobile-only gate so the
 * improved enquiry form shows on desktop too. It does NOT bypass A/B membership — it is only honoured
 * via {@link useShouldShowLIVE_27666_EnquiryForm}, so control stays control on every viewport.
 * SSR-safe — returns `false` until mounted, and reacts to `storage` events (incl. same-tab dispatch).
 */
export const useLIVE_27666_DesktopOverride = (): boolean => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (env.LIVE) return undefined;

    const read = () =>
      setEnabled(window.localStorage.getItem(LIVE_27666_DESKTOP_OVERRIDE_KEY) === 'true');
    read();
    window.addEventListener('storage', read);
    return () => window.removeEventListener('storage', read);
  }, []);

  return enabled;
};

export const useIsLIVE_27666_EnquiryFormImprovementsEnabled = () => {
  const variant = useSelector(getVariant_LIVE_27666_EnquiryFormImprovements);

  return variant === '1';
};

/**
 * Single source of truth for whether to render the improved enquiry form on a given surface
 * (modal and profile sidebar). New UI is mobile-only; the dev override lifts that gate on desktop
 * for variant members only. Control stays control on every viewport.
 */
export const useShouldShowLIVE_27666_EnquiryForm = (): boolean => {
  const { isMobile } = useSelector(getScreenType);
  const desktopOverride = useLIVE_27666_DesktopOverride();
  const isEnabled = useIsLIVE_27666_EnquiryFormImprovementsEnabled();

  return isEnabled && (isMobile || desktopOverride);
};
